import { MsalProvider, useMsal } from '@azure/msal-react';
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { msalInstance } from './msalInstance';
import OneDriveModal from './OneDriveModal';
import { PickedItem } from './SharePointButton';
import { Logger } from 'src/utils/Logger';
import { useSessionStorage } from '@mantine/hooks';

const PENDING_PICKER_KEY = 'sharepoint-pending-picker';
const PENDING_CALLBACK_PATH_KEY = 'sharepoint-pending-callback-path';
const PENDING_FILE_EXTENSIONS_KEY = 'sharepoint-pending-file-extensions';

type SharePointContextType = {
  openPicker: (onPicked: (items: PickedItem[]) => void) => void;
  pendingPickedItems: PickedItem[] | null;
  clearPendingPickedItems: () => void;
  setPendingPicker: (value: boolean) => void;
  setSavedPath: (value: string) => void;
  setSavedFileExtensions: (value: string[] | undefined) => void;
};

const SharePointContext = createContext<SharePointContextType | null>(null);

export function useSharePointPicker() {
  const context = useContext(SharePointContext);
  if (!context) {
    throw new Error('useSharePointPicker must be used within SharePointProvider');
  }
  return context;
}

export function useSharePointSession() {
  const context = useContext(SharePointContext);
  if (!context) {
    throw new Error('useSharePointSession must be used within SharePointProvider');
  }
  return {
    setPendingPicker: context.setPendingPicker,
    setSavedPath: context.setSavedPath,
    setSavedFileExtensions: context.setSavedFileExtensions,
  };
}

type SharePointProviderProps = {
  children: React.ReactNode;
};

function SharePointProviderInner({ children }: SharePointProviderProps) {
  const { accounts } = useMsal();
  const isAuthenticated = accounts.length > 0;

  const [modalOpen, setModalOpen] = useState(false);
  const [pendingPickedItems, setPendingPickedItems] = useState<PickedItem[] | null>(null);
  const [isPostRedirect, setIsPostRedirect] = useState(false);
  const hasCheckedRef = useRef(false);
  const callbackRef = useRef<((items: PickedItem[]) => void) | null>(null);

  const [pendingPicker, setPendingPicker, removePendingPicker] = useSessionStorage<boolean>({
    key: PENDING_PICKER_KEY,
    defaultValue: false,
    getInitialValueInEffect: false,
  });

  const [savedPath, setSavedPath, removeSavedPath] = useSessionStorage<string>({
    key: PENDING_CALLBACK_PATH_KEY,
    defaultValue: '',
    getInitialValueInEffect: false,
  });

  const [savedFileExtensions, setSavedFileExtensions, removeSavedFileExtensions] =
    useSessionStorage<string[] | undefined>({
      key: PENDING_FILE_EXTENSIONS_KEY,
      defaultValue: undefined,
      getInitialValueInEffect: false,
    });

  // Check if we should open the modal after returning from login redirect
  useEffect(() => {
    if (hasCheckedRef.current) return;

    if (!pendingPicker) {
      hasCheckedRef.current = true;
      return;
    }

    Logger.info(
      `SharePointProvider: Checking pending picker. isAuthenticated=${isAuthenticated}, savedPath=${savedPath}, currentPath=${window.location.pathname}`,
    );

    if (isAuthenticated && savedPath === window.location.pathname) {
      Logger.info('SharePointProvider: Opening modal after login redirect');
      const savedExtensions = sessionStorage.getItem(PENDING_FILE_EXTENSIONS_KEY);
      if (savedExtensions) {
        try {
          setSavedFileExtensions(JSON.parse(savedExtensions));
        } catch {
          // Ignore parse errors
        }
      }

      setModalOpen(true);
      setIsPostRedirect(true);
      removePendingPicker();
      removeSavedPath();
      hasCheckedRef.current = true;
    }
  }, [isAuthenticated, pendingPicker, savedPath, removePendingPicker, removeSavedPath]);

  const openPicker = useCallback(
    (onPicked: (items: PickedItem[]) => void) => {
      callbackRef.current = onPicked;
      setSavedPath(window.location.pathname);
      setModalOpen(true);
    },
    [setSavedPath],
  );

  const handlePickedItems = useCallback(
    (items: PickedItem[]) => {
      setModalOpen(false);
      if (isPostRedirect) {
        // After redirect, store items for consuming components to pick up
        setPendingPickedItems(items);
        setIsPostRedirect(false);
      } else {
        // Normal flow - call the callback directly
        callbackRef.current?.(items);
      }
      callbackRef.current = null;
    },
    [isPostRedirect],
  );

  const handleClose = useCallback(() => {
    setModalOpen(false);
    setIsPostRedirect(false);
    removeSavedFileExtensions();
    callbackRef.current = null;
  }, [removeSavedFileExtensions]);

  const clearPendingPickedItems = useCallback(() => {
    setPendingPickedItems(null);
  }, []);

  return (
    <SharePointContext
      value={{
        openPicker,
        pendingPickedItems,
        clearPendingPickedItems,
        setPendingPicker,
        setSavedPath,
        setSavedFileExtensions,
      }}
    >
      {children}
      {modalOpen && (
        <OneDriveModal
          opened={modalOpen}
          onClose={handleClose}
          onPickedItems={handlePickedItems}
          fileExtensions={savedFileExtensions}
        />
      )}
    </SharePointContext>
  );
}

export function SharePointProvider({ children }: SharePointProviderProps) {
  return (
    <MsalProvider instance={msalInstance}>
      <SharePointProviderInner>{children}</SharePointProviderInner>
    </MsalProvider>
  );
}
